/**
 * Phase 4 — offline intent queue.
 *
 * When the offline-axios wrapper sees that a money-moving POST was
 * issued while the device is offline, the intent is pushed here. Each
 * intent is stored in IndexedDB so it survives a page reload (and
 * indeed a browser restart). When connectivity returns — detected via
 * the connectivity event source — the queue is drained: each intent is
 * replayed against the server in order, and the local outcome cache is
 * updated.
 *
 * Safety property
 * ===============
 * Every queued intent carries the Idempotency-Key the form generated
 * when the user clicked "submit". If the request was already accepted
 * (e.g. the SW background-flushed it before this page came back), the
 * server returns the cached response from the Phase 0 idempotency
 * middleware — no double-debit. The same property covers the user
 * pressing the dashboard's "retry now" button by hand.
 *
 * What is NOT in the queue
 * ========================
 * Step-up-requiring actions (above-threshold transfers, cross-border,
 * grant payouts) are NEVER queued offline — the WebAuthn assertion
 * needs the server's challenge and can't be deferred. The offline-axios
 * wrapper refuses to queue these and surfaces a "go online to confirm"
 * error to the caller.
 */

const DB_NAME    = 'mojo-offline';
const DB_VERSION = 1;
const STORE      = 'intents';

const STATUS_QUEUED  = 'queued';
const STATUS_SENDING = 'sending';
const STATUS_DONE    = 'done';
const STATUS_FAILED  = 'failed';

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available — running in node?'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('byStatus',  'status', { unique: false });
        store.createIndex('byCreated', 'createdAt', { unique: false });
      }
    };
  });
  return _dbPromise;
}

function tx(db, mode = 'readonly') {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older runtimes the tests sometimes hit.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Push a new intent. `payload` is the axios config: { url, method,
 * data, headers }. `idempotencyKey` is the same UUID the form
 * generated; we re-attach it on every replay so the server dedups.
 */
export async function enqueueIntent({ url, method = 'POST', data, headers = {}, idempotencyKey, action, amountZar, displayedSummary }) {
  const db = await openDb();
  const row = {
    id:               uuid(),
    url, method, data, headers,
    idempotencyKey,
    action, amountZar, displayedSummary,
    status:           STATUS_QUEUED,
    attempts:         0,
    createdAt:        Date.now(),
    lastAttemptAt:    null,
    lastError:        null,
  };
  await new Promise((res, rej) => {
    const r = tx(db, 'readwrite').add(row);
    r.onsuccess = () => res();
    r.onerror   = () => rej(r.error);
  });
  return row;
}

export async function listIntents(filterStatus = null) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const store = tx(db);
    const out = [];
    const cur = store.openCursor();
    cur.onsuccess = (event) => {
      const c = event.target.result;
      if (!c) { res(out); return; }
      if (!filterStatus || c.value.status === filterStatus) out.push(c.value);
      c.continue();
    };
    cur.onerror = () => rej(cur.error);
  });
}

export async function getIntent(id) {
  const db = await openDb();
  return new Promise((res, rej) => {
    const r = tx(db).get(id);
    r.onsuccess = () => res(r.result || null);
    r.onerror   = () => rej(r.error);
  });
}

async function _update(id, patch) {
  const db = await openDb();
  const existing = await getIntent(id);
  if (!existing) return null;
  const updated = { ...existing, ...patch };
  await new Promise((res, rej) => {
    const r = tx(db, 'readwrite').put(updated);
    r.onsuccess = () => res();
    r.onerror   = () => rej(r.error);
  });
  return updated;
}

export async function markSending(id)          { return _update(id, { status: STATUS_SENDING, lastAttemptAt: Date.now() }); }
export async function markDone(id, response)   { return _update(id, { status: STATUS_DONE,    response, lastAttemptAt: Date.now() }); }
export async function markFailed(id, error)    { return _update(id, { status: STATUS_FAILED,  lastError: String(error || ''), lastAttemptAt: Date.now(), attempts: undefined /* set below */ }); }

export async function bumpAttempt(id, error) {
  const db = await openDb();
  const existing = await getIntent(id);
  if (!existing) return null;
  const updated = {
    ...existing,
    status:        STATUS_QUEUED,   // back to queued so the next drain retries
    attempts:      (existing.attempts || 0) + 1,
    lastAttemptAt: Date.now(),
    lastError:     String(error || ''),
  };
  await new Promise((res, rej) => {
    const r = tx(db, 'readwrite').put(updated);
    r.onsuccess = () => res();
    r.onerror   = () => rej(r.error);
  });
  return updated;
}

export async function deleteIntent(id) {
  const db = await openDb();
  await new Promise((res, rej) => {
    const r = tx(db, 'readwrite').delete(id);
    r.onsuccess = () => res();
    r.onerror   = () => rej(r.error);
  });
}

/**
 * Drain the queue. Caller passes a `send(intent) -> Promise<response>`
 * function so the queue stays transport-agnostic (axios in prod, mocked
 * fetch in tests).
 *
 * Returns a summary { sent, succeeded, failed }. Stops early when a
 * send throws a `network` flavour error so we don't burn through every
 * intent if connectivity dropped again mid-drain.
 */
export async function drainQueue(send, { maxRetries = 5 } = {}) {
  const pending = (await listIntents(STATUS_QUEUED))
    .filter((i) => (i.attempts || 0) < maxRetries)
    .sort((a, b) => a.createdAt - b.createdAt);
  const summary = { sent: 0, succeeded: 0, failed: 0, intents: [] };
  for (const intent of pending) {
    await markSending(intent.id);
    summary.sent += 1;
    try {
      const response = await send(intent);
      await markDone(intent.id, response);
      summary.succeeded += 1;
      summary.intents.push({ id: intent.id, status: 'done' });
    } catch (e) {
      const looksLikeNetwork = String(e?.message || '').toLowerCase().includes('network')
        || e?.code === 'ECONNABORTED'
        || (!e?.response && e?.request);
      if (looksLikeNetwork) {
        // Bump attempts (so we don't retry forever) and bail; we'll
        // pick up where we left off when the next online event fires.
        await bumpAttempt(intent.id, e);
        summary.failed += 1;
        summary.intents.push({ id: intent.id, status: 'requeued', error: String(e) });
        break;
      }
      // 4xx / 5xx from the server: not a transient failure. Mark
      // failed so the UI can surface a "this transaction was rejected"
      // toast and stop hammering the endpoint.
      await markFailed(intent.id, e);
      summary.failed += 1;
      summary.intents.push({ id: intent.id, status: 'failed', error: String(e) });
    }
  }
  return summary;
}

export const STATUS = {
  QUEUED:  STATUS_QUEUED,
  SENDING: STATUS_SENDING,
  DONE:    STATUS_DONE,
  FAILED:  STATUS_FAILED,
};
